import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { site, socials } from '../content';
import { useLocalTime } from '../hooks/useLocalTime';
import { useTheme } from '../hooks/useTheme';
import { scrollToTarget, setScrollLocked } from '../hooks/useSmoothScroll';
import { EASE_IN_OUT, EASE_OUT } from './motion';
import { RollText } from './Reveal';

const links = [
  { label: 'About', target: '#about' },
  { label: 'Now', target: '#now' },
  { label: 'Collective', target: '#collective' },
  { label: 'Contact', target: '#contact' },
];

export function Nav({ show }: { show: boolean }) {
  const time = useLocalTime(site.timeZone);
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setScrollLocked(open);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const go = (target: string) => {
    setOpen(false);
    // let the menu start closing before the page moves
    setTimeout(() => scrollToTarget(target), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        className="nav wrap"
        initial={{ y: '-120%' }}
        animate={{ y: show ? '0%' : '-120%' }}
        transition={{ duration: 1, ease: EASE_OUT, delay: 0.6 }}
      >
        <button className="nav__brand" onClick={() => go('#top')} aria-label="Back to top">
          <RollText>{site.name}</RollText>
        </button>

        <p className="nav__meta mono">
          <span className="nav__pulse" aria-hidden="true" />
          {site.location} <span className="muted">·</span> {time}
        </p>

        <nav className="nav__links mono" aria-label="Primary">
          {links.map((link) => (
            <button key={link.label} onClick={() => go(link.target)}>
              <RollText>{link.label}</RollText>
            </button>
          ))}
          <button
            className="nav__theme"
            onClick={toggle}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            <span className="nav__theme-icon" data-theme-icon={theme} />
          </button>
        </nav>

        <button
          className="nav__menu mono"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="menu"
        >
          {open ? 'Close' : 'Menu'}
        </button>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="menu"
            className="menu wrap"
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.8, ease: EASE_IN_OUT }}
          >
            <nav className="menu__links" aria-label="Menu">
              {links.map((link, i) => (
                <span className="reveal-mask" key={link.label}>
                  <motion.button
                    onClick={() => go(link.target)}
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '110%', transition: { duration: 0.3 } }}
                    transition={{ duration: 0.9, ease: EASE_OUT, delay: 0.25 + i * 0.07 }}
                  >
                    <span className="mono muted">0{i + 1}</span>
                    {link.label}
                  </motion.button>
                </span>
              ))}
            </nav>
            <motion.div
              className="menu__foot mono"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.6 } }}
              exit={{ opacity: 0 }}
            >
              <a href={`mailto:${site.email}`}>{site.email}</a>
              <div>
                {socials.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer">
                    {s.label}
                  </a>
                ))}
                <button onClick={toggle}>{theme === 'dark' ? 'Light' : 'Dark'} mode</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
